import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Stack,
  Switch,
  ToastId,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  useNotificationToast,
  useOrganizationCloudRenderingPolicies,
} from "../hooks";
import { BrowserStreamingProtocol, UserPreferences } from "../hooks/types";
import { useUpdateUserPreferencesMutation } from "../hooks/useUpdateUserPreferencesMutation";
import { useBrowserStreamingProtocolSettingQuery } from "../streamer/dcv/useBrowserStreamingProtocolSettingQuery";
import { isChangeDisallowedByPolicy } from "../utils/policy-evaluation";

let activeToastId: ToastId | undefined;
export function StreamingTechnologyPreferences() {
  const { t } = useTranslation();
  const toast = useNotificationToast();
  const browserStreamingProtocolSettingQuery =
    useBrowserStreamingProtocolSettingQuery();
  const {
    data: policies,
    isPending: arePoliciesPending,
    isError: arePoliciesError,
  } = useOrganizationCloudRenderingPolicies();
  const updateUserPreferencesMutation = useUpdateUserPreferencesMutation();
  const updateUserPreferences = useCallback(
    (preferences: Pick<UserPreferences, "browserStreamingProtocol">) => {
      updateUserPreferencesMutation.mutate(preferences, {
        onSuccess: () => {
          activeToastId && toast.close(activeToastId);
          activeToastId = toast({
            title: t("streamingPreferences.dialog.savedTitle"),
            description: t(
              "streamingPreferences.dialog.savedStreamingProtocol",
            ),
            status: "success",
          });
        },
        onError: () => {
          activeToastId && toast.close(activeToastId);
          activeToastId = toast({
            title: t("streamingPreferences.dialog.errorTitle"),
            description: t("streamingPreferences.dialog.error"),
            status: "error",
          });
        },
      });
    },
    [toast, t, updateUserPreferencesMutation],
  );

  return (
    <FormControl
      as="fieldset"
      isDisabled={
        browserStreamingProtocolSettingQuery.isLoading ||
        arePoliciesPending ||
        isChangeDisallowedByPolicy(policies?.streamingGatewayPolicy)
      }
      isInvalid={
        browserStreamingProtocolSettingQuery.isError || arePoliciesError
      }
    >
      <Stack spacing={4} paddingTop={4}>
        <HStack>
          <Switch
            id="use-dcv"
            isChecked={
              browserStreamingProtocolSettingQuery.data ===
              BrowserStreamingProtocol.DCV
            }
            onChange={(evt) =>
              updateUserPreferences({
                browserStreamingProtocol: evt.target.checked
                  ? BrowserStreamingProtocol.DCV
                  : null,
              })
            }
          />
          <FormLabel htmlFor="use-dcv" margin={0}>
            {t("streamingPreferences.protocols.use_dcv")}
          </FormLabel>
        </HStack>
        <FormHelperText marginTop={0}>
          {t("streamingPreferences.protocols.use_dcv_help_text_disabled")}
        </FormHelperText>
        <FormErrorMessage>
          {t("streamingPreferences.protocols.use_dcv_loading_error")}
        </FormErrorMessage>
      </Stack>
    </FormControl>
  );
}
